// Ottawa-area neighbourhood SEO landing pages.
//
// Long-tail queries like "trailer rental Kanata" outperform "trailer rental
// Ottawa Kanata" by a wide margin. We ship one page per high-volume Ottawa
// neighbourhood with localized copy, FAQ schema, and a live-listings strip.
//
// The first three entries (kanata, south-ottawa, orleans) deliberately target
// the same areas as Robertson Rentall's three yard locations — the angle is
// "trailer rental at your neighbour's driveway, not a 45-minute round trip to
// a rental yard."

export type NeighbourhoodSlug =
  | 'kanata'
  | 'south-ottawa'
  | 'orleans'
  | 'barrhaven'
  | 'stittsville'
  | 'nepean'
  | 'westboro'
  | 'hintonburg'
  | 'manotick';

export interface Neighbourhood {
  slug: NeighbourhoodSlug;
  /** Display name. */
  name: string;
  /** Pretty parenthetical for the H1 (e.g. "Kanata, Ottawa"). */
  fullName: string;
  /** ISO city for the parent breadcrumb. */
  parentCitySlug: 'ottawa';
  /** Approximate geocoded center for the search query. */
  lat: number;
  lng: number;
  /** One-line hero subhead. */
  tagline: string;
  /** Postal-code prefixes (FSAs) that fall within the area — used in copy. */
  fsas: string[];
  /** Sub-neighbourhoods / streets / landmarks rendered as chips. */
  subAreas: string[];
  /** Common renter use cases. */
  useCases: string[];
  /** Trailer types most-rented in this neighbourhood (slug + label + emoji). */
  popularTypes: Array<{ slug: string; label: string; emoji: string; example: string }>;
  /** Local "why TowShare" bullets — should be unique per neighbourhood. */
  whyHere: string[];
  /** Reference to local rental yards we're positioning against (no link). */
  competitorAddresses?: string[];
  /** FAQ entries — emitted as FAQPage JSON-LD. */
  faqs: Array<{ q: string; a: string }>;
}

const SHARED_FAQS = (n: string) => [
  {
    q: `Where can I rent a trailer in ${n} today?`,
    a: `TowShare lists privately owned trailers across ${n}. Search by postal code or street and the map shows everyone within 10 km, including same-day pickups when owners enable instant book.`,
  },
  {
    q: `How is this different from a rental yard in ${n}?`,
    a: `Rental yards are open 8-5 on weekdays and require a 20-40 minute drive each way. TowShare trailers live on neighbours' driveways throughout ${n} — pickup is usually within 5-10 minutes of you, and many owners allow evening or weekend handoffs.`,
  },
  {
    q: `Do I need a special licence to tow in Ontario?`,
    a: `In Ontario, a regular G-class licence is enough for trailers under 4,600 kg GVWR. Most utility, dump, and enclosed trailers fall well under that. Listing detail pages show the GVWR and recommended hitch class.`,
  },
  {
    q: `Is there insurance?`,
    a: `Every booking is backed by TowShare's $1,500 Trailer Guarantee. The renter's auto policy typically extends third-party liability to a rented trailer being towed — confirm with your broker.`,
  },
];

export const NEIGHBOURHOODS: Record<NeighbourhoodSlug, Neighbourhood> = {
  // --- Robertson Rentall target locations ----------------------------------

  kanata: {
    slug: 'kanata',
    name: 'Kanata',
    fullName: 'Kanata, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.3088,
    lng: -75.8983,
    tagline:
      'Trailers in your driveway — not at the Michael Cowpland Drive yard. Kanata renters skip the lineup and pick up from a neighbour within minutes.',
    fsas: ['K2K', 'K2L', 'K2M', 'K2T', 'K2V', 'K2W'],
    subAreas: [
      'Kanata North',
      'Beaverbrook',
      'Bridlewood',
      'Glen Cairn',
      'Katimavik',
      'Morgan\'s Grant',
      'Marchwood',
      'Lakeside',
      'Hazeldean',
    ],
    useCases: [
      'IKEA Pinecrest furniture pickups',
      'Trail Road landfill dump runs from Bridlewood and Glen Cairn',
      'Calabogie / Madawaska ATV and dirt-bike transport',
      'Kanata North tech-park office moves',
      'Hazeldean and Stittsville bedroom renovations',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Dump & moving runs' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Renos & landscaping' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Furniture & gear' },
      { slug: 'car_hauler', label: 'Car hauler', emoji: '🚗', example: 'Project cars' },
    ],
    competitorAddresses: ['255 Michael Cowpland Dr, Kanata, ON, K2M 0M5'],
    whyHere: [
      'Kanata has TowShare\'s densest cluster of owners outside downtown — most renters find a trailer within 10 minutes of their home, vs. a 30-minute round trip to the Michael Cowpland Drive yard.',
      'Hourly bookings are common in Kanata for short IKEA Pinecrest or Trail Road runs — typically $20–$40 versus the full-day yard rate.',
      'Same-day pickups available; many owners enable instant book so you don\'t wait for a response.',
    ],
    faqs: [
      {
        q: 'Where can I rent a utility trailer in Kanata?',
        a: 'TowShare has utility-trailer owners across Bridlewood, Glen Cairn, Beaverbrook, Hazeldean, Morgan\'s Grant, and Kanata North. Most are within 10 km of any Kanata address. Search by postal code or street name and the map will show what\'s available within the hour.',
      },
      {
        q: 'Is there a trailer rental near Michael Cowpland Drive?',
        a: 'Several. Owners with utility and dump trailers list throughout Kanata North, including listings on streets running off Terry Fox Dr and Klondike Rd. Filter the map for your pickup point and you\'ll usually see 3–6 options within 5 km.',
      },
      ...SHARED_FAQS('Kanata'),
    ],
  },

  'south-ottawa': {
    slug: 'south-ottawa',
    name: 'South Ottawa',
    fullName: 'South Ottawa (Hunt Club / Riverside South)',
    parentCitySlug: 'ottawa',
    lat: 45.345,
    lng: -75.665,
    tagline:
      'Hunt Club, Riverside South, South Keys, Greenboro and Findlay Creek — TowShare owners across south-end Ottawa, no detour past the Del Zotto Avenue yard.',
    fsas: ['K1G', 'K1T', 'K1V', 'K1X', 'K2C', 'K4M'],
    subAreas: [
      'Hunt Club',
      'Riverside South',
      'South Keys',
      'Greenboro',
      'Findlay Creek',
      'Blossom Park',
      'Manor Village',
      'Heron Gate',
      'Quinterra',
    ],
    useCases: [
      'Trail Road landfill dump runs from Findlay Creek and Riverside South',
      'Costco Strandherd / Bank St runs',
      'Airport-area equipment hauls',
      'Greely / Manotick cottage-property moves',
      'Hunt Club bungalow basement renovations',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Daily hauls' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Demo & landscaping' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Moves & gear' },
      { slug: 'car_hauler', label: 'Car hauler', emoji: '🚗', example: 'Project cars' },
    ],
    competitorAddresses: ['2610 Del Zotto Ave, Ottawa, ON, K1T 3V7'],
    whyHere: [
      'South-end Ottawa is geographically spread out — driving to the Del Zotto Avenue yard from Findlay Creek or Riverside South can eat 40 minutes round-trip before you even start hauling. TowShare puts a trailer within 5–10 km of you.',
      'Heavy weekend renovation demand in Hunt Club and Riverside South — dump and 16′+ utility trailers are abundant on Saturday mornings.',
      'Cheaper than yard rentals for short jobs — most South Ottawa owners price by the hour for trips under 4 hours.',
    ],
    faqs: [
      {
        q: 'Where can I rent a dump trailer in Hunt Club or Riverside South?',
        a: 'Dump trailers are TowShare\'s most-requested category in south Ottawa — owners cluster around Findlay Creek, Riverside South, and Hunt Club. Filter the map for /search?type=dump and your postal code to see what\'s within 10 km.',
      },
      {
        q: 'Is there trailer rental near Del Zotto Avenue?',
        a: 'TowShare lists across Hunt Club, Greenboro, and Blossom Park — typically within 5 km of Del Zotto. Unlike a yard, pickup happens at the owner\'s house and many accept evening/weekend handoffs.',
      },
      ...SHARED_FAQS('South Ottawa'),
    ],
  },

  orleans: {
    slug: 'orleans',
    name: 'Orléans',
    fullName: 'Orléans, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.4581,
    lng: -75.5167,
    tagline:
      'Trailer rental across Orléans — Avalon, Fallingbrook, Convent Glen, Chapel Hill. No drive to the Vanguard Drive yard.',
    fsas: ['K1C', 'K1E', 'K1W', 'K4A'],
    subAreas: [
      'Avalon',
      'Fallingbrook',
      'Chapel Hill',
      'Convent Glen',
      'Orléans Wood',
      'Queenswood Heights',
      'Cumberland',
      'Cardinal Creek',
      'Notting Hill',
    ],
    useCases: [
      'Cumberland Heritage Village / Petrie Island runs',
      'Trail Road and Carp landfill dump runs',
      'Boat trailer haul-outs from Petrie Island / Cumberland marina',
      'Notting Hill and Avalon new-build landscaping',
      'Snowmobile transport to Calabogie / Mont Tremblant',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'General hauls' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Landscaping' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Furniture & gear' },
      { slug: 'snowmobile', label: 'Snowmobile', emoji: '❄️', example: 'Sled transport' },
    ],
    competitorAddresses: ['236 Vanguard Dr, Orléans, ON, K4A 3V6'],
    whyHere: [
      'TowShare owners are spread across Orléans — most renters find a trailer 5–10 minutes away versus the Vanguard Drive yard trek across the 174.',
      'New-build communities like Avalon, Notting Hill, and Cardinal Creek drive heavy landscape/dump demand in spring and summer.',
      'Strong supply of snowmobile and boat haulers tied to Petrie Island and Cumberland marina trips.',
    ],
    faqs: [
      {
        q: 'Where can I rent a trailer in Orléans for landscaping?',
        a: 'Landscape and dump trailers are the most-rented in Orléans, especially across Avalon, Notting Hill, and Fallingbrook. TowShare owners commonly list 7\'x12\' and 7\'x14\' dumps for $80–$120/day.',
      },
      {
        q: 'Is there trailer rental near Vanguard Drive?',
        a: 'Yes — TowShare lists across Cardinal Creek, Convent Glen, and Chapel Hill, all within 5 km of Vanguard Dr. Pickup is at the owner\'s home, so you skip yard hours and lineup time.',
      },
      ...SHARED_FAQS('Orléans'),
    ],
  },

  // --- Broader Ottawa coverage --------------------------------------------

  barrhaven: {
    slug: 'barrhaven',
    name: 'Barrhaven',
    fullName: 'Barrhaven, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.2745,
    lng: -75.7361,
    tagline:
      'Trailer rental across Barrhaven — Stonebridge, Half Moon Bay, Longfields, Chapman Mills. Same-day pickup from a neighbour.',
    fsas: ['K2J', 'K2G'],
    subAreas: [
      'Stonebridge',
      'Half Moon Bay',
      'Longfields',
      'Chapman Mills',
      'Davidson Heights',
      'Heritage Park',
      'Old Barrhaven',
    ],
    useCases: [
      'Stonebridge landscaping and mulch hauls',
      'Costco Barrhaven (Strandherd) bulk pickups',
      'Half Moon Bay new-build construction debris',
      'Manotick / Greely cottage runs',
      'Trail Road landfill dump runs',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Costco & dump runs' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Landscaping & demo' },
      { slug: 'landscape', label: 'Landscape', emoji: '🌿', example: 'Mulch & soil' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Moves' },
    ],
    whyHere: [
      'Barrhaven has Ottawa\'s highest concentration of new-build homes — landscape and dump trailers are in heavy supply March through October.',
      'Strandherd Drive corridor means you\'re close to Costco Barrhaven; many renters use a utility trailer once and save the cost in a single trip.',
      'Manotick and Greely cottage-property owners list dump and flatbed trailers that Barrhaven renters can borrow for renos.',
    ],
    faqs: [
      {
        q: 'Where can I rent a trailer in Barrhaven?',
        a: 'TowShare lists trailers across Stonebridge, Half Moon Bay, Longfields, and Chapman Mills. Most pickups are within 5 km of any Barrhaven address — search by postal code to see what\'s available today.',
      },
      ...SHARED_FAQS('Barrhaven'),
    ],
  },

  stittsville: {
    slug: 'stittsville',
    name: 'Stittsville',
    fullName: 'Stittsville, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.2616,
    lng: -75.9182,
    tagline:
      'Trailer rental in Stittsville — Jackson Trails, Wyldewood, Fairwinds. Closer than Kanata, faster than a yard.',
    fsas: ['K2S'],
    subAreas: [
      'Jackson Trails',
      'Wyldewood',
      'Fairwinds',
      'Crossing Bridge',
      'Old Stittsville',
      'Amberwood',
    ],
    useCases: [
      'Carp Road landfill dump runs',
      'Calabogie / Madawaska Highlands ATV transport',
      'New-build landscape and patio jobs',
      'Hazeldean / Kanata commuter moves',
      'Mississippi Lake cottage runs',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Daily hauls' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Landscaping' },
      { slug: 'snowmobile', label: 'Snowmobile', emoji: '❄️', example: 'Sled to Calabogie' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Moves & ATVs' },
    ],
    whyHere: [
      'Stittsville owners list more snowmobile and ATV trailers per capita than anywhere else in Ottawa — Calabogie and the Madawaska Highlands are an easy day trip.',
      'Carp landfill is 10 minutes north — dump-trailer rentals turn into a 30-minute total job.',
      'Jackson Trails and Wyldewood new-builds drive ongoing landscape and renovation demand.',
    ],
    faqs: [
      {
        q: 'Where can I rent a snowmobile trailer in Stittsville?',
        a: 'TowShare snowmobile-trailer owners cluster in Jackson Trails, Wyldewood, and Old Stittsville. Filter /search?type=snowmobile by Stittsville postal — single and double-sled trailers list November through March.',
      },
      ...SHARED_FAQS('Stittsville'),
    ],
  },

  nepean: {
    slug: 'nepean',
    name: 'Nepean',
    fullName: 'Nepean, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.3475,
    lng: -75.7569,
    tagline:
      'Trailer rental across Nepean — Bells Corners, Centrepointe, Crestview, Manordale. Closer than the rental yard.',
    fsas: ['K2A', 'K2B', 'K2C', 'K2E', 'K2G', 'K2H', 'K2J', 'K2R'],
    subAreas: [
      'Bells Corners',
      'Centrepointe',
      'Crestview',
      'Manordale',
      'Tanglewood',
      'Knollsbrook',
      'Lakeview Park',
      'Trend-Arlington',
    ],
    useCases: [
      'IKEA Pinecrest pickups',
      'Algonquin College student moves',
      'Costco Merivale runs',
      'Trail Road landfill dump runs',
      'Bells Corners renovation debris',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'IKEA & dump runs' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Furniture & gear' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Renovations' },
      { slug: 'car_hauler', label: 'Car hauler', emoji: '🚗', example: 'Project cars' },
    ],
    whyHere: [
      'Nepean is the geographic centre of west Ottawa — most rentals here put a trailer within 5 km of you regardless of which corner you live in.',
      'IKEA Pinecrest is a 10-minute drive — utility trailers turn an "impossible" furniture haul into a single afternoon.',
      'Algonquin College move-in/out demand keeps enclosed-trailer supply high in late August and early May.',
    ],
    faqs: [
      {
        q: 'Where can I rent a trailer in Nepean?',
        a: 'TowShare lists trailers across Bells Corners, Centrepointe, Crestview, Manordale, and Tanglewood. Filter the map by your postal code; most pickups are within 5–8 km.',
      },
      ...SHARED_FAQS('Nepean'),
    ],
  },

  westboro: {
    slug: 'westboro',
    name: 'Westboro',
    fullName: 'Westboro, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.3925,
    lng: -75.7625,
    tagline:
      'Trailer rental in Westboro — apartments, infill renos, weekend cottage runs. Walk-up neighbourhood pickups.',
    fsas: ['K1Z', 'K2A'],
    subAreas: [
      'Westboro Village',
      'McKellar Park',
      'Champlain Park',
      'Highland Park',
      'Hampton Park',
    ],
    useCases: [
      'Westboro condo / apartment moves',
      'Infill construction debris hauls',
      'Quebec cottage runs (Wakefield, Chelsea)',
      'IKEA Pinecrest pickups',
      'Mountain bike + gear transport to Gatineau Park',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Moves & dump' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Furniture' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Reno debris' },
      { slug: 'motorcycle', label: 'Motorcycle', emoji: '🏍️', example: 'Gatineau Park' },
    ],
    whyHere: [
      'Dense, walkable streets — many TowShare pickups in Westboro are a 2-minute walk from your door if you live in the Village.',
      'Heavy infill construction means dump trailers are listed almost year-round.',
      'Closer to the Champlain Bridge than any rental yard — Wakefield/Chelsea cottage runs are 30 minutes shorter when you skip the yard.',
    ],
    faqs: [
      {
        q: 'Where can I rent a trailer in Westboro?',
        a: 'TowShare lists trailers across Westboro Village, McKellar Park, Champlain Park, and Highland Park. Pickup is typically within walking distance for residents in the Village core.',
      },
      ...SHARED_FAQS('Westboro'),
    ],
  },

  hintonburg: {
    slug: 'hintonburg',
    name: 'Hintonburg',
    fullName: 'Hintonburg & Mechanicsville, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.4023,
    lng: -75.7269,
    tagline:
      'Trailer rental in Hintonburg, Mechanicsville, and Wellington West. Tight streets, easy pickups, no yard.',
    fsas: ['K1Y', 'K1Z'],
    subAreas: [
      'Hintonburg',
      'Mechanicsville',
      'Wellington West',
      'Tunney\'s Pasture',
      'Civic Hospital',
    ],
    useCases: [
      'Apartment / townhouse moves',
      'Wellington West infill construction',
      'Studio gear hauling for local musicians',
      'Quebec ski-trip transport',
      'IKEA pickups',
    ],
    popularTypes: [
      { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Local moves' },
      { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Gear & furniture' },
      { slug: 'motorcycle', label: 'Motorcycle', emoji: '🏍️', example: 'Bikes' },
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Reno debris' },
    ],
    whyHere: [
      'Hintonburg renters need trailers small enough to fit on tight streets — utility and enclosed dominate the local inventory.',
      'Renters can avoid the long drive to a yard; most TowShare pickups in Hintonburg are within 1 km.',
      'Strong supply of enclosed trailers for the area\'s gigging musicians and small businesses.',
    ],
    faqs: [
      {
        q: 'Can I rent a small trailer for moving in Hintonburg?',
        a: 'Yes — 4\'x8\' and 5\'x8\' utility trailers are TowShare\'s most-rented size in Hintonburg, easy to manoeuvre on narrow side streets. Most owners price these around $30–$50 per day.',
      },
      ...SHARED_FAQS('Hintonburg'),
    ],
  },

  manotick: {
    slug: 'manotick',
    name: 'Manotick',
    fullName: 'Manotick & Greely, Ottawa',
    parentCitySlug: 'ottawa',
    lat: 45.2231,
    lng: -75.6845,
    tagline:
      'Manotick and Greely — rural-property and waterfront-friendly trailer rentals. Bigger trailers, more space, real neighbours.',
    fsas: ['K4M', 'K0A', 'K1V'],
    subAreas: [
      'Manotick Village',
      'Manotick Estates',
      'Greely',
      'Mahogany',
      'Findlay Creek',
      'Rideau Forest',
    ],
    useCases: [
      'Rideau River boat haul-outs',
      'Greely / Manotick property landscaping',
      'Snowmobile and ATV transport',
      'Large furniture / equipment moves',
      'Horse trailer / ag-equipment moves',
    ],
    popularTypes: [
      { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Landscaping' },
      { slug: 'landscape', label: 'Landscape', emoji: '🌿', example: 'Mulch & soil' },
      { slug: 'car_hauler', label: 'Car hauler', emoji: '🚗', example: 'Project cars' },
      { slug: 'flatbed', label: 'Flatbed', emoji: '🏗️', example: 'Lumber & skids' },
    ],
    whyHere: [
      'Manotick / Greely TowShare owners list larger trailers (18′+ enclosed, 14′ dumps, flatbeds) that simply don\'t fit at urban yards.',
      'Rideau River frontage drives heavy boat-trailer demand spring and fall.',
      'Rural property owners can list ag-equipment trailers that aren\'t available at any commercial yard.',
    ],
    faqs: [
      {
        q: 'Where can I rent a large dump or flatbed trailer in Manotick?',
        a: 'Manotick and Greely TowShare owners often have 14\'+ dumps and 20\' flatbeds available — bigger than anything stocked at city rental yards. Filter /search?type=dump or /search?type=flatbed for Manotick postal codes.',
      },
      ...SHARED_FAQS('Manotick'),
    ],
  },
};

export const NEIGHBOURHOOD_LIST: Neighbourhood[] = Object.values(NEIGHBOURHOODS);

/** Ottawa-only convenience list (currently every neighbourhood). */
export const OTTAWA_NEIGHBOURHOODS = NEIGHBOURHOOD_LIST.filter(
  (n) => n.parentCitySlug === 'ottawa',
);
